<template>
  <Drawer
    v-model:visible="visible"
    :header="header"
    :modal="true"
    closable
    :position="'bottom'"
    :style="{ width: '100vw', height: 'auto' }"
  >
    <Tabs value="0" class="w-full">
      <TabList>
        <Tab value="0">Today</Tab>
        <Tab value="1">Week</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="0">
          <div class="overflow-auto h-[50vh]">
            <!-- Today's Date Header -->
            <div class="flex items-center justify-center text-xl font-semibold text-gray-700 mb-4">
              {{ DateTime.fromISO(new Date().toISOString()).toFormat('cccc d LLLL yyyy') }}
            </div>
            <div
              v-if="summaryAsArraySorted.length > 0"
              class="flex flex-row gap-2 pb-8"
              v-for="event in summaryAsArraySorted"
              :key="event.description"
            >
              <div class="flex items justify-between w-full text-sm text-gray-700">
                <div
                  :class="`flex flex-col w-full text-${event.impact > 0 ? 'green' : 'red'}-700 font-semibold`"
                >
                  <span class="text-ellipsis overflow-hidden whitespace-nowrap max-w-30">{{
                    event.description
                  }}</span>

                  <div class="w-full">
                    <ProgressBar
                      :value="(Math.abs(event.impact) / 120) * 100"
                      pt:label="overflow-auto"
                      pt:root="!h-7 !rounded-full"
                      :pt:value:class="{
                        '!bg-primary-500': event.impact >= 0,
                        '!bg-red-300': event.impact < 0,
                      }"
                    >
                      {{
                        Math.abs(event.impact) < 30
                          ? event.impact
                          : Math.abs(event.impact) >= 120
                            ? `${Math.floor(event.impact / 60)}h ${Math.abs(Math.floor(event.impact % 60))}m`
                            : `${event.impact}mins`
                      }}
                    </ProgressBar>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="flex items-center justify-center text-sm text-gray-500 pt-4">
                No screen time events today.
              </div>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
      <TabPanel value="1">
        <div class="flex items-center justify-center text-sm text-gray-500 pt-4 w-full">
          <div class="flex flex-col items-center w-full">
            <label for="weekCommencing" class="text-sm text-gray-700 mb-2">
              Select Week Commencing:
            </label>
            <select
              id="weekCommencing"
              v-model="weekCommencing"
              class="border border-gray-300 rounded-md p-2"
            >
              <option v-for="date in weekCommencingChoices" :key="date.value" :value="date.value">
                {{ date.label }}
              </option>
            </select>
            <Divider />
            <div class="flex flex-col gap-2 w-full">
              <div
                v-for="(day, index) in Object.keys(weeklySummary)"
                :key="index"
                class="grid grid-cols-5 items-centerw-full text-sm text-gray-700"
              >
                <span>{{ day }}</span>
                <span
                  :class="{
                    'text-center': true,
                    'text-green-700': weeklySummary[day].inc > 0,
                  }"
                >
                  {{ weeklySummary[day].inc > 0 ? '+' : ''
                  }}{{ weeklySummary[day].inc ? weeklySummary[day].inc : '' }}
                </span>
                <span>
                  <span
                    v-for="(reason, index) in weeklySummary[day].inc_reasons"
                    :key="index"
                    class="text-xs text-gray-500"
                    >{{ reason
                    }}<span v-if="index < weeklySummary[day].inc_reasons.length - 1">, </span></span
                  >
                </span>
                <span
                  :class="{
                    'text-center': true,
                    'text-red-700': weeklySummary[day].dec > 0,
                  }"
                >
                  {{ weeklySummary[day].dec > 0 ? '-' : ''
                  }}{{ weeklySummary[day].dec ? weeklySummary[day].dec : '' }}
                </span>
                <span>
                  <span
                    v-for="(reason, index) in weeklySummary[day].dec_reasons"
                    :key="index"
                    class="text-xs text-gray-500"
                    >{{ reason
                    }}<span v-if="index < weeklySummary[day].dec_reasons.length - 1">, </span></span
                  >
                </span>
              </div>
            </div>
          </div>
        </div>
      </TabPanel>
    </Tabs>
  </Drawer>
</template>

<script setup lang="ts">
  import Drawer from 'primevue/drawer'
  import Tabs from 'primevue/tabs'
  import TabList from 'primevue/tablist'
  import TabPanel from 'primevue/tabpanel'
  import TabPanels from 'primevue/tabpanels'
  import Tab from 'primevue/tab'
  import Child from '../store/models/Child'
  import { computed, ref, Ref } from 'vue'
  import { Divider, ProgressBar } from 'primevue'
  import { DateTime } from 'luxon'
  import ScreenTimeEvent from '../store/models/ScreenTimeEvent'
  import { event } from '@primeuix/themes/aura/timeline'

  const visible = defineModel<boolean>('visible')
  const weekCommencing = ref<Date | null>(null)

  const props = defineProps<{
    child: Child | null
  }>()

  const header = computed(() => {
    return `${props.child?.name} ScreenTime Summary`
  })

  const screenTimeEventsToday = computed(() => {
    if (!props.child || !props.child.screenTimeEvents) {
      return []
    }
    const screenTimeEvents = props.child.screenTimeEvents.filter((event: ScreenTimeEvent) => {
      if (!event.timestamp) {
        return false
      }
      const eventDate = new Date(event.timestamp)
      const today = new Date()
      return (
        eventDate.getDate() === today.getDate() &&
        eventDate.getMonth() === today.getMonth() &&
        eventDate.getFullYear() === today.getFullYear()
      )
    })
    return screenTimeEvents
  })

  const eventsSummarisedByDescription = computed(() => {
    const events = screenTimeEventsToday.value
    const summary = {}
    events.forEach((event) => {
      if (!summary[event.description]) {
        summary[event.description] = 0
      }
      summary[event.description] += event.impact
    })
    return summary
  })
  const summaryAsArraySorted = computed(() => {
    const summary = eventsSummarisedByDescription.value
    const summaryArray = Object.entries(summary).map(([description, impact]) => {
      return { description, impact }
    })
    summaryArray.sort(
      (a: { description; impact }, b: { description; impact }) => b.impact - a.impact
    )
    return summaryArray
  }) as Ref<{ description: string; impact: number }[]>

  const weekCommencingChoices = computed(() => {
    if (!props.child || !props.child.screenTimeEvents) {
      return []
    }
    const earliestDate = new Date(
      Math.min(
        ...props.child.screenTimeEvents.map((event: ScreenTimeEvent) => {
          if (!event.timestamp) {
            return 0
          }
          return new Date(event.timestamp).getTime()
        })
      )
    )
    const latestDate = new Date(
      Math.max(
        ...props.child.screenTimeEvents.map((event: ScreenTimeEvent) => {
          if (!event.timestamp) {
            return 0
          }
          return new Date(event.timestamp).getTime()
        })
      )
    )

    const weekCommencingDates = [] as Date[] // Starting on Mondays
    const currentDate = new Date(earliestDate)

    while (currentDate <= latestDate) {
      const weekStart = new Date(currentDate)
      weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1) // Set to Monday
      weekCommencingDates.push(weekStart)
      currentDate.setDate(currentDate.getDate() + 7)
    }

    return weekCommencingDates.map((date) => {
      return {
        value: date,
        label: DateTime.fromJSDate(date).toFormat('cccc d LLLL yyyy'),
      }
    })
  })

  const weeklySummary = computed(() => {
    // Returns an object keyed by weekday 'Monday', 'Tuesday', etc.
    // Each day has a inc key and a dec key, plus inc_reasons and dec_reasons
    // The inc key is the sum of positive impacts that day, inc_reasons is an array of reasons
    // The dec key is the sum of negative impacts that day, dec_reasons is an array of reasons

    if (!props.child || !props.child.screenTimeEvents || !weekCommencing.value) {
      return []
    }

    const weekdayOffset = 0
    const weekStart = weekCommencing.value
    const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000) // Add 7 days to get the end of the week

    const events = props.child.screenTimeEvents.filter((event: ScreenTimeEvent) => {
      if (!event.timestamp) {
        return false
      }
      const eventDate = DateTime.fromJSDate(new Date(event.timestamp))
      return eventDate >= weekStart && eventDate < weekEnd
    })

    const summary = {
      Monday: { inc: 0, dec: 0, inc_reasons: [], dec_reasons: [] },
      Tuesday: { inc: 0, dec: 0, inc_reasons: [], dec_reasons: [] },
      Wednesday: { inc: 0, dec: 0, inc_reasons: [], dec_reasons: [] },
      Thursday: { inc: 0, dec: 0, inc_reasons: [], dec_reasons: [] },
      Friday: { inc: 0, dec: 0, inc_reasons: [], dec_reasons: [] },
      Saturday: { inc: 0, dec: 0, inc_reasons: [], dec_reasons: [] },
      Sunday: { inc: 0, dec: 0, inc_reasons: [], dec_reasons: [] },
    }

    events.forEach((event: ScreenTimeEvent) => {
      if (!event.timestamp) {
        return
      }
      const eventDate = DateTime.fromJSDate(new Date(event.timestamp))
      const weekday = eventDate.toFormat('cccc')
      if (event.impact > 0) {
        summary[weekday].inc += event.impact
        summary[weekday].inc_reasons.push(event.description)
      } else {
        summary[weekday].dec += Math.abs(event.impact)
        summary[weekday].dec_reasons.push(event.description)
      }
    })

    return summary
  })
</script>
